// TourCommonFields/index.tsx

import {
    Checkbox,
    Col, Form, Input, Radio, Row, Select, Space, Spin, Typography,
} from 'antd';
import React, {
    useCallback, useEffect, useState,
} from 'react';

import { ETourTargetType } from '../../../../enums/tours.enum';
import useDebounce from '../../../../hooks/useDebounce';
import { Tour } from '../../../../interfaces/tours';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const PAGE_SIZE = 20;

export interface TourCommonFieldsProps {
    tourSelected: Tour | null;
    countries: any[];
    loadingCountries: boolean;
    fetchWorkspacesAsync?: (params: {
        page: number;
        pageSize: number;
        search?: string;
        workspaceIDs?: string[]
    }) => Promise<{ items: any[]; total: number }>;
    targetType: ETourTargetType;
    onChangeTargetType: (value: ETourTargetType) => void;
    extraRightTop?: React.ReactNode;
    // Nuevos props para manejar "new workspaces"
    applyToNewWorkspacesOnly?: boolean;
    onChangeApplyToNewWorkspaces?: (value: boolean) => void;
}

const rules = [
    { required: true, message: 'This field is required' },
];

const TourCommonFields: React.FC<TourCommonFieldsProps> = ({
    tourSelected,
    countries,
    loadingCountries,
    fetchWorkspacesAsync,
    targetType,
    onChangeTargetType,
    extraRightTop,
    applyToNewWorkspacesOnly = false,
    onChangeApplyToNewWorkspaces,
}) => {
    const [workspacePage, setWorkspacePage] = useState(1);
    const [workspaceSearch, setWorkspaceSearch] = useState('');
    const [workspaceHasMore, setWorkspaceHasMore] = useState(true);
    const [workspaceLoadingMore, setWorkspaceLoadingMore] = useState(false);
    const [workspaceOptions, setWorkspaceOptions] = useState<any[]>([]);

    const handleWorkspaceScroll = async (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 32 && !workspaceLoadingMore && workspaceHasMore && fetchWorkspacesAsync) {
            setWorkspaceLoadingMore(true);
            const nextPage = workspacePage + 1;
            const data = await fetchWorkspacesAsync({
                page: nextPage,
                pageSize: PAGE_SIZE,
                search: workspaceSearch,
            });
            setWorkspaceOptions((prev) => {
                const ids = new Set(prev.map((ws: any) => ws.id));
                const newItems = (data.items || []).filter((ws: any) => !ids.has(ws.id));
                return [...prev, ...newItems];
            });
            setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            setWorkspacePage(nextPage);
            setWorkspaceLoadingMore(false);
        }
    };

    const debouncedSearch = useDebounce<string>(workspaceSearch, 800);

    const handleWorkspaceSearch = useCallback((value: string) => {
        setWorkspaceOptions([]);
        setWorkspaceSearch(value);
        setWorkspacePage(1);
    }, []);

    const fetchWorkspacesTourOptions = async (ids: string[]) => {
        setWorkspaceLoadingMore(true);
        if (fetchWorkspacesAsync) {
            const data = await fetchWorkspacesAsync({
                page: 1,
                pageSize: PAGE_SIZE,
                search: debouncedSearch,
                workspaceIDs: ids,
            });
            setWorkspaceOptions(
                (data.items || []).filter(
                    (item: any, idx: number, arr: any[]) => arr.findIndex((i) => i._id === item._id) === idx,
                ),
            );
            setWorkspaceLoadingMore(false);
        }
    };

    useEffect(() => {
        const run = async () => {
            const hasQuery = typeof debouncedSearch === 'string' && debouncedSearch.trim().length > 0;
            if (!hasQuery) {
                return;
            }
            setWorkspaceLoadingMore(true);
            if (fetchWorkspacesAsync) {
                const data = await fetchWorkspacesAsync({
                    page: 1,
                    pageSize: PAGE_SIZE,
                    search: debouncedSearch,
                });
                setWorkspaceOptions(
                    (data.items || []).filter(
                        (item: any, idx: number, arr: any[]) => arr.findIndex((i) => i._id === item._id) === idx,
                    ),
                );
                setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            }
            setWorkspaceLoadingMore(false);
        };
        run();
    }, [debouncedSearch]);

    useEffect(() => {
        if (!tourSelected) {
            setWorkspaceSearch('');
            setWorkspacePage(1);
            setWorkspaceHasMore(true);
            setWorkspaceLoadingMore(false);
            setWorkspaceOptions([]);
        }
    }, [tourSelected]);

    const handleWorkspaceDropdownVisibleChange = async (open: boolean) => {
        if (open && workspaceOptions.length === 0 && targetType === ETourTargetType.WORKSPACE && fetchWorkspacesAsync) {
            setWorkspaceLoadingMore(true);
            const data = await fetchWorkspacesAsync({
                page: 1,
                pageSize: PAGE_SIZE,
                search: workspaceSearch,
            });
            setWorkspaceOptions(data.items || []);
            setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            setWorkspaceLoadingMore(false);
        }
    };

    useEffect(() => {
        if (tourSelected && tourSelected.targetType === ETourTargetType.WORKSPACE) {
            fetchWorkspacesTourOptions(tourSelected?.targetIds || []);
        }
    }, [tourSelected]);

    return (
        <Row gutter={[32, 24]}>
            <Col span={12}>
                <Form.Item label="Tour Name" name="name" rules={rules}>
                    <Input placeholder="Tour Name" />
                </Form.Item>
                <Form.Item label="Tour Description" name="description" rules={rules}>
                    <TextArea placeholder="Tour Description" rows={4} maxLength={255} showCount />
                </Form.Item>
                <Form.Item name="loomUrl" label="Loom Url" rules={rules}>
                    <Input placeholder="https://www.loom.com/share/1234567890" type="url" />
                </Form.Item>
                <Form.Item name="scribeUrl" label="Scribe Url (optional)">
                    <Input placeholder="https://www.scribe.com/1234567890" type="url" />
                </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {extraRightTop && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {extraRightTop}
                    </Space>
                )}

                <Form.Item name="targetType" label="Define the target">
                    <Radio.Group value={targetType} onChange={(e) => onChangeTargetType(e.target.value)}>
                        <Radio value={ETourTargetType.COUNTRY}>Country</Radio>
                        <Radio value={ETourTargetType.WORKSPACE}>Workspace</Radio>
                    </Radio.Group>
                </Form.Item>

                {/* ===== NUEVO: Checkbox para workspaces nuevos ===== */}
                {targetType === ETourTargetType.WORKSPACE && onChangeApplyToNewWorkspaces && (
                    <Form.Item
                        label="Workspace Targeting"
                        tooltip="Control which workspaces will see this tour"
                    >
                        <Checkbox
                            checked={applyToNewWorkspacesOnly}
                            onChange={(e) => onChangeApplyToNewWorkspaces(e.target.checked)}
                        >
                            <Space direction="vertical" size={2}>
                                <Text strong style={{ fontSize: '14px' }}>
                                    Apply to new workspaces only
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Tour will only appear for workspaces created after today
                                </Text>
                            </Space>
                        </Checkbox>
                    </Form.Item>
                )}

                <Form.Item
                    label={targetType === ETourTargetType.COUNTRY ? 'Select Countries' : 'Select Workspaces'}
                    name="targetIds"
                    rules={[{
                        required: !applyToNewWorkspacesOnly, // No requerido si es "solo nuevos"
                        message: targetType === ETourTargetType.COUNTRY
                            ? 'Select at least one country'
                            : 'Select at least one workspace',
                    }]}
                    help={
                        applyToNewWorkspacesOnly && targetType === ETourTargetType.WORKSPACE
                            ? 'Disabled because apply to new workspaces only'
                            : undefined
                    }
                >
                    {targetType === ETourTargetType.COUNTRY ? (
                        <Select
                            mode="multiple"
                            placeholder="Select countries..."
                            loading={loadingCountries}
                            allowClear
                        >
                            {countries.map((country: any) => (
                                <Option key={country.id} value={country.id}>
                                    {country.name}
                                    {' '}
                                    (
                                    {country.code}
                                    )
                                </Option>
                            ))}
                        </Select>
                    ) : (
                        <Select
                            mode="multiple"
                            disabled={applyToNewWorkspacesOnly}
                            placeholder="Select workspaces..."
                            loading={workspaceLoadingMore}
                            allowClear
                            showSearch
                            filterOption={false}
                            onSearch={handleWorkspaceSearch}
                            onPopupScroll={handleWorkspaceScroll}
                            onDropdownVisibleChange={handleWorkspaceDropdownVisibleChange}
                            notFoundContent={workspaceLoadingMore ? <Spin size="small" /> : null}
                            value={workspaceOptions.map((ws: any) => ws._id)}
                        >
                            {workspaceOptions.map((ws: any) => (
                                <Option key={ws._id} value={ws._id}>
                                    {ws.name}
                                </Option>
                            ))}
                        </Select>
                    )}
                </Form.Item>
            </Col>
        </Row>
    );
};

export default TourCommonFields;
